import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { MagicGrid, MagicCard } from '../components/MagicBento/MagicBento';
import { supabase } from '../supabase/supabase';
import { miniSaasProjects as staticProjects, categories } from '../data/projects';
import { appCache } from '../lib/cache';

const { FiSearch, FiExternalLink, FiChevronLeft, FiChevronRight, FiFilter, FiCheck, FiX, FiPlus } = FiIcons;
const ITEMS_PER_PAGE = 48;

const Home = () => {
  const [allTools, setAllTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [sortBy, setSortBy] = useState('date');
  const [filteredSaas, setFilteredSaas] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchTools = async () => {
      setLoading(true);
      if (appCache.tools) {
        setAllTools(appCache.tools);
        setLoading(false);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('saas_projects_1776800000001')
          .select('*')
          .eq('status', 'approved') // Only fetch approved tools for the directory
          .order('dateAdded', { ascending: false });

        if (data && data.length > 0) {
          appCache.tools = data;
          setAllTools(data);
        } else {
          setAllTools(staticProjects);
        }
      } catch (err) {
        console.error('Failed to fetch tools:', err);
        setAllTools(staticProjects);
      } finally {
        setLoading(false);
      }
    };
    fetchTools();
  }, []);

  useEffect(() => {
    let filtered = allTools.filter(saas => 
      saas.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      saas.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (saas.tags || []).some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (selectedCategories.length > 0) {
      filtered = filtered.filter(saas => selectedCategories.includes(saas.category));
    }

    filtered.sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      return new Date(b.dateAdded) - new Date(a.dateAdded);
    });

    let featuredItems = filtered.filter(f => f.featured);
    let normalItems = filtered.filter(f => !f.featured);

    if (featuredItems.length > 3) {
      const demoted = featuredItems.slice(3).map(item => ({...item, featured: false}));
      normalItems = [...demoted, ...normalItems];
      featuredItems = featuredItems.slice(0, 3);
    }

    normalItems.sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      return new Date(b.dateAdded) - new Date(a.dateAdded);
    });

    setFilteredSaas([...featuredItems, ...normalItems]);
    setCurrentPage(1);
  }, [allTools, searchTerm, selectedCategories, sortBy]);

  const toggleCategory = (catId) => {
    setSelectedCategories(prev => 
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  };

  const handleProjectClick = async (saas) => {
    window.open(saas.url, '_blank');
    if (window.gtag) {
      window.gtag('event', 'click', {
        'event_category': 'Outbound Link',
        'event_label': saas.name,
        'transport_type': 'beacon'
      });
    }
    try {
      await supabase.from('click_events').insert({
        project_id: String(saas.id),
        project_name: saas.name
      });
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentItems = filteredSaas.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSaas.length / ITEMS_PER_PAGE);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="w-full">
      {/* Hero Section */}
      <motion.section 
        initial={{ opacity: 0, y: 50 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.8 }} 
        className="relative z-10 py-20 px-4 text-center"
      >
        <div className="flex justify-center mb-8">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="group relative inline-flex items-center gap-3 px-4 py-2 rounded-full bg-[#150a28]/80 border border-purple-500/30 backdrop-blur-md shadow-[0_0_20px_rgba(147,51,234,0.15)] transition-all duration-300 cursor-default"
          >
            <div className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]"></span>
            </div>
            <div className="flex items-center gap-2 text-xs md:text-sm font-medium">
              <span className="text-white font-bold">{allTools.length} Apps</span>
              <span className="w-1 h-1 rounded-full bg-white/20"></span>
              <span className="bg-gradient-to-r from-purple-200 to-indigo-200 bg-clip-text text-transparent">
                Adding more...
              </span>
            </div>
          </motion.div>
        </div>

        <motion.h2 
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 100 }}
          className="text-5xl md:text-7xl font-bold mb-6"
        >
          <span className="bg-gradient-to-r from-purple-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
            Mini AI SaaS
          </span>
          <br />
          <span className="text-white/90 text-4xl md:text-6xl">Directory</span>
        </motion.h2>

        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-lg text-white/50 max-w-2xl mx-auto"
        >
          A curated collection of exceptional AI tools.
        </motion.p>

        {/* Submit Tool Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 flex justify-center"
        >
          <Link 
            to="/submit-tool" 
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-black rounded-full shadow-[0_0_30px_rgba(147,51,234,0.4)] hover:shadow-[0_0_40px_rgba(147,51,234,0.6)] hover:scale-105 transition-all duration-300 text-lg border border-purple-400/30"
          >
            <SafeIcon icon={FiPlus} className="text-xl" />
            Submit a Tool
          </Link>
        </motion.div>
      </motion.section>

      {/* Search and Filters */}
      <motion.section 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12"
      >
        <div className="bg-[#0c0518] rounded-2xl border border-[#392e4e] p-4 md:p-6 shadow-2xl">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
            
            {/* Search */}
            <div className="relative md:col-span-6 lg:col-span-7">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/30" />
              <input
                type="text"
                placeholder="Search tools..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[#150a28] border border-[#392e4e] rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all"
              />
            </div>

            {/* Multi-select Category Dropdown */}
            <div className="relative md:col-span-3 lg:col-span-3" ref={dropdownRef}>
              <button
                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-[#150a28] border border-[#392e4e] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
              >
                <div className="flex items-center gap-2 truncate">
                  <SafeIcon icon={FiFilter} className="text-white/40" />
                  <span className="truncate">
                    {selectedCategories.length === 0 ? 'All Categories' : `${selectedCategories.length} Selected`}
                  </span>
                </div>
                <SafeIcon 
                  icon={isCategoryDropdownOpen ? FiChevronLeft : FiChevronRight} 
                  className={`transform transition-transform ${isCategoryDropdownOpen ? '-rotate-90' : 'rotate-90'} text-white/40`} 
                />
              </button>

              {/* Dropdown Menu */}
              <AnimatePresence>
                {isCategoryDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute z-[100] top-full left-0 right-0 mt-2 bg-[#150a28] border border-[#392e4e] rounded-xl shadow-2xl overflow-hidden"
                  >
                    <div className="max-h-60 overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-purple-600 scrollbar-track-[#0c0518]">
                      {categories.filter(c => c.id !== 'all').map((cat) => (
                        <label 
                          key={cat.id} 
                          className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 rounded-lg cursor-pointer transition-colors group"
                        >
                          <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${selectedCategories.includes(cat.id) ? 'bg-purple-600 border-purple-500' : 'bg-[#0c0518] border-[#392e4e] group-hover:border-purple-500/50'}`}>
                            {selectedCategories.includes(cat.id) && <SafeIcon icon={FiCheck} className="text-white text-xs" />}
                          </div>
                          <span className="text-sm text-white/80 group-hover:text-white flex-1">{cat.name}</span>
                        </label>
                      ))}
                    </div>
                    {selectedCategories.length > 0 && (
                      <div className="p-2 border-t border-[#392e4e] bg-[#0c0518]">
                        <button 
                          onClick={() => setSelectedCategories([])}
                          className="w-full py-2 text-xs font-bold text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          Clear Selection
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="md:col-span-3 lg:col-span-2 px-4 py-3 bg-[#150a28] border border-[#392e4e] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all cursor-pointer"
            >
              <option value="date">Sort by Newest</option>
              <option value="name">Sort by Name</option>
            </select>
          </div>

          {/* Active Filter Tags */}
          {selectedCategories.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {selectedCategories.map(catId => {
                const cat = categories.find(c => c.id === catId);
                return (
                  <span key={catId} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs font-medium">
                    {cat?.name}
                    <button onClick={() => toggleCategory(catId)} className="hover:text-white transition-colors">
                      <SafeIcon icon={FiX} />
                    </button>
                  </span>
                )
              })}
            </div>
          )}
        </div>
      </motion.section>

      {/* Magic Bento Grid */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <AnimatePresence mode='wait'>
            {filteredSaas.length > 0 ? (
              <>
                <MagicGrid>
                  {currentItems.map((saas, index) => {
                    const isVidroid = saas.name === 'Vidroid';
                    return (
                      <motion.div
                        key={saas.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.3, delay: (index % ITEMS_PER_PAGE) * 0.05 }}
                        onClick={() => handleProjectClick(saas)}
                        className="h-full cursor-pointer flex flex-col"
                      >
                        <MagicCard 
                          className={`h-full flex flex-col ${isVidroid ? 'magic-bento-card--flashy' : ''}`}
                          glowColor={isVidroid ? '255, 215, 0' : undefined}
                        >
                          <div className="-mt-[1.5em] -mx-[1.5em] mb-4 h-40 relative overflow-hidden border-b border-[#392e4e] rounded-t-2xl flex-shrink-0">
                            {saas.image_url ? (
                              <img src={saas.image_url} alt={saas.name} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-red-600 via-purple-600 to-blue-600 flex items-center justify-center p-4">
                                <h3 className="text-2xl md:text-3xl font-black text-white drop-shadow-lg text-center leading-tight tracking-tight">
                                  {saas.name}
                                </h3>
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[var(--background-dark)] to-transparent" />
                          </div>

                          <div className="magic-bento-card__header mb-3 flex justify-between items-center">
                            <span className="magic-bento-card__label text-purple-400 font-bold tracking-wider text-[10px]">
                              {categories.find(c => c.id === saas.category)?.name || saas.category}
                            </span>
                            {saas.featured && (
                              <span className={`px-2 py-0.5 rounded-full text-white text-[10px] font-bold uppercase shadow-lg ${isVidroid ? 'bg-yellow-500 text-black shadow-yellow-500/40' : 'bg-animated-gradient shadow-purple-500/20'}`}>
                                Featured
                              </span>
                            )}
                          </div>

                          <div className="magic-bento-card__content flex-grow flex flex-col">
                            {saas.image_url && (
                              <h3 className="magic-bento-card__title leading-tight mb-2">
                                {saas.name}
                              </h3>
                            )}
                            <p className="text-sm text-white/70 line-clamp-3 mb-4 leading-relaxed">
                              {saas.description}
                            </p>
                            
                            <div className="mt-auto pt-2">
                              <div className="flex gap-2 flex-wrap mb-4 max-h-6 overflow-hidden">
                                {(saas.tags || []).slice(0, 3).map((tag, i) => (
                                  <span key={i} className="px-2 py-1 rounded-md bg-white/5 border border-white/5 whitespace-nowrap text-[10px] text-white/50 font-medium">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                              <button 
                                onClick={(e) => { e.stopPropagation(); handleProjectClick(saas); }}
                                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-red-600 via-purple-600 to-blue-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-purple-500/40 hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
                              >
                                Check it out <SafeIcon icon={FiExternalLink} className="text-white/80" />
                              </button>
                            </div>
                          </div>
                        </MagicCard>
                      </motion.div>
                    );
                  })}
                </MagicGrid>

                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-4 mt-12">
                    <button 
                      onClick={() => paginate(currentPage - 1)} 
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg bg-[#150a28] border border-[#392e4e] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-purple-600/20 transition-all"
                    >
                      <SafeIcon icon={FiChevronLeft} className="text-xl" />
                    </button>
                    <div className="flex items-center gap-2">
                      <span className="text-white/60 text-sm">Page</span>
                      <span className="bg-purple-600/20 px-3 py-1 rounded-md border border-purple-500/30 text-white font-bold">
                        {currentPage}
                      </span>
                      <span className="text-white/60 text-sm">of {totalPages}</span>
                    </div>
                    <button 
                      onClick={() => paginate(currentPage + 1)} 
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg bg-[#150a28] border border-[#392e4e] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-purple-600/20 transition-all"
                    >
                      <SafeIcon icon={FiChevronRight} className="text-xl" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
                <SafeIcon icon={FiSearch} className="text-6xl text-white/10 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white/50 mb-2">No projects found</h3>
                <p className="text-white/30 text-sm">Try adjusting your search criteria</p>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </section>
    </div>
  );
};

export default Home;